import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { AuthContext } from "../context/AuthContext";
import getOrders from "../api/app/getOrders";

const PaymentSuccessPage = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQrCode, setShowQrCode] = useState(false);

  const navigate = useNavigate();
  const { storeId } = useParams();
  const theme = useTheme();
  const { token } = useContext(AuthContext);

  const main = theme.palette.info.main;

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    let alive = true;
    const fetchLatestOrder = async () => {
      try {
        setLoading(true);
        const response = await getOrders();
        if (alive && response?.data?.length > 0) {
          // Get the most recent order (first in the list)
          const latestOrder = response.data[0];
          setOrder(latestOrder);

          // Check if QR code should be shown (10 minutes before start time until end time)
          const eightHoursMs = 8 * 60 * 60 * 1000;
          const now = new Date(Date.now() + eightHoursMs);
          const start = new Date(latestOrder.startTime);
          const end = new Date(latestOrder.endTime);
          const tenMinutesMs = 10 * 60 * 1000;
          setShowQrCode(now >= start.getTime() - tenMinutesMs && now <= end);
        }
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchLatestOrder();
    return () => {
      alive = false;
    };
  }, [token]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: theme.palette.background.default }}
      >
        <CircularProgress color="info" />
      </Box>
    );
  }

  const orderId = order?.order?.id ?? order?.id;
  const simulatorName = order?.storeSimulator?.name ?? order?.name ?? "未知";
  const storeName = order?.storeSimulator?.store?.name ?? order?.storeName ?? "";
  const startTime = order?.startTime;
  const endTime = order?.endTime;
  const amount = order?.amount;

  return (
    <Box
      width="100%"
      minHeight="100vh"
      sx={{ backgroundColor: theme.palette.background.default }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.appbar || theme.palette.background.default,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            aria-label="back"
            onClick={() => navigate(`/app/store/${storeId}`)}
            sx={{ color: theme.palette.text.primary }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{ pl: 2, fontSize: "h6.fontSize", fontWeight: "bold" }}
            color="text.primary"
          >
            預約完成
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        maxWidth={480}
        mx="auto"
        mt={4}
        px={2}
      >
        {/* Success Icon */}
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 80, color: main, mb: 2 }}
          />
          <Typography
            variant="h5"
            fontWeight="bold"
            color="text.primary"
            align="center"
          >
            付款成功
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mt: 1 }}
          >
            感謝您的預約！
          </Typography>
        </Box>

        {order ? (
          <Card
            variant="outlined"
            sx={{
              bgcolor: theme.palette.background.default,
              color: theme.palette.text.primary,
              borderColor: theme.palette.divider,
              boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight="bold" color="text.primary" gutterBottom>
                訂單資訊
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                包廂：{simulatorName}
              </Typography>

              {storeName && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  店家：{storeName}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                編號：{orderId}
              </Typography>

              <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                {`訂位時間：${formatInTimeZone(parseISO(startTime), "UTC", "yyyy/MM/dd HH:mm")} ~ ${formatInTimeZone(
                  parseISO(endTime),
                  "UTC",
                  "yyyy/MM/dd HH:mm"
                )}`}
              </Typography>

              <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, fontWeight: 700 }}>
                {`總計：$${amount}`}
              </Typography>

              {/* 入場碼 */}
              <Box sx={{ mt: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Typography
                  variant="subtitle1"
                  color="text.primary"
                  sx={{ mb: 1.5, fontWeight: 700 }}
                >
                  入場碼
                </Typography>
                {showQrCode ? (
                  <Box
                    sx={{
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: `0 0 16px ${main}66`,
                      p: 2,
                    }}
                  >
                    <QRCodeSVG value={orderId} size={160} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      py: 3,
                      px: 4,
                      border: `2px dashed ${theme.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      align="center"
                    >
                      入場碼於入場前10分鐘顯示
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mt: 4 }}
          >
            {token ? "找不到訂單資訊" : "請先登入"}
          </Typography>
        )}

        {/* Action Buttons */}
        <Box display="flex" flexDirection="column" gap={1.5} mt={4} mb={6}>
          <Button
            fullWidth
            variant="contained"
            color="info"
            size="large"
            onClick={() => navigate("/app/myOrder")}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            查看我的訂單
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="info"
            size="large"
            onClick={() => navigate(`/app/store/${storeId}`)}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            返回首頁
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PaymentSuccessPage;
